/* eslint-disable max-lines */
import { useMemo } from 'react';
import styled from 'styled-components';

import { FiscalPrintEnabledQuery } from '@dreryk/egabinet-invoice-frontend-api';
import {
   MedicalServiceWithPractitioners,
   MedicalServiceWithPractitionersQuery,
   Practitioner,
   TaxRate,
} from '@dreryk/egabinet-private-medical-service-frontend-api';

import { useCqrsFind, useCqrsGet } from '@dreryk/command-query-react-common';
import { FormContextValues, useFormController } from '@dreryk/react-common';
import { FormController, Message, MessageType } from '@dreryk/react-components';

import { protectFeature } from '~libs.shared.system';

import { PageLoader } from '../../shared/components/PageLoader';
import { useCqrsSearchPractitionerProvidingPrivateMedicalService } from '../../shared/queries/useCqrsSearchPractitionerProvidingPrivateMedicalServiceQuery';
import { medicalServiceTranslationKeys } from '../../translations';
import { MedicalServiceDefaultPriceField } from '../shared/defaultPrice/MedicalServiceDefaultPriceField';
import { MedicalServiceFixedValueField } from '../shared/fixedValue/MedicalServiceFixedValueField';
import { MedicalServiceFormBean } from '../shared/form/medicalServiceForm.types';
import { MedicalServiceNameForInvoicingField } from '../shared/nameForInvoicing/MedicalServiceNameForInvoicingField';
import { MedicalServiceNameForInvoicingTextView } from '../shared/nameForInvoicing/MedicalServiceNameForInvoicingTextView';
import { MedicalServiceUsingNameAsNameForInvoicingField } from '../shared/nameForInvoicing/MedicalServiceUsingNameAsNameForInvoicingField';
import { nameForInvoicingPartVisible } from '../shared/nameForInvoicing/nameForInvocing.utils';
import { MedicalServiceNameForReceiptTextView } from '../shared/nameForReceipt/MedicalServiceNameForReceiptTextView';
import { MedicalServiceNameForReceiptField } from '../shared/nameForReceipt/fields/MedicalServiceNameForReceiptField';
import { MedicalServiceNameForReceiptTypeField } from '../shared/nameForReceipt/fields/MedicalServiceNameForReceiptTypeField';
import { getNameForReceipt, nameForReceiptPartVisible } from '../shared/nameForReceipt/nameForReceipt.utils';
import { usePractitionersSynchronization } from '../shared/practitioner/practitioner.hooks';
import { mapPractitionerMedicalServiceToMedicalServicePractitionerBean } from '../shared/practitioner/practitioner.utils';
import { MedicalServicePractitionerField } from '../shared/practitioner/single/MedicalServicePractitionerField';
import { MedicalServicePractitionerInteractiveTable } from '../shared/practitioner/table/MedicalServicePractitionerInteractiveTable';
import { MedicalServiceProportionalValueField } from '../shared/proportionalValue/MedicalServiceProportionalValueField';
import { useCqrsSearchTaxRate } from '../shared/queries/useCqrsSearchTaxRate';
import { MedicalServiceNameField } from '../shared/serviceName/MedicalServiceNameField';
import { useNotUniqueMedicalServiceNameErrorHandler } from '../shared/serviceName/serviceName.hooks';
import { MedicalServiceSettlementTypeField } from '../shared/settlement/MedicalServiceSettlementTypeField';
import { MedicalServiceSettlementTypeVisitor } from '../shared/settlement/settlement.types';
import { createMedicalServiceSettlementTypeVisitor } from '../shared/settlement/settlement.utils';
import { MedicalServiceTaxRateField } from '../shared/taxRate/MedicalServiceTaxRateField';
import { getTaxRate } from '../shared/taxRate/taxRate.utils';

type MedicalServiceUpdateFormLoaderRenderProps = {
   taxRates: TaxRate[];
   practitioners: Practitioner[];
   medicalService: MedicalServiceWithPractitioners;
   fiscalPrintEnabled: boolean;
};

type MedicalServiceUpdateFormLoaderProps = {
   readonly id: number;
   readonly children: (renderProps: MedicalServiceUpdateFormLoaderRenderProps) => JSX.Element;
};

export const MedicalServiceUpdateFormLoader = (props: MedicalServiceUpdateFormLoaderProps): JSX.Element | null => {
   const invoiceCreateAvailable = protectFeature('creatingInvoices');

   const {
      data: medicalService,
      loading: medicalServiceLoading,
      error: medicalServiceError,
   } = useCqrsGet<MedicalServiceWithPractitioners>(() => {
      const query = new MedicalServiceWithPractitionersQuery();
      query.id = props.id;
      return query;
   });
   const { data: taxRates, loading: taxRatesLoading, error: taxRatesError } = useCqrsSearchTaxRate();
   const {
      data: practitioners,
      loading: practitionersLoading,
      error: practitionersError,
   } = useCqrsSearchPractitionerProvidingPrivateMedicalService();

   const {
      data: fiscalPrintEnabled,
      loading: fiscalPrintEnabledLoading,
      error: fiscalPrintEnabledError,
   } = useCqrsFind(() => new FiscalPrintEnabledQuery(), { enabled: invoiceCreateAvailable });

   if (medicalServiceLoading || taxRatesLoading || practitionersLoading || fiscalPrintEnabledLoading) {
      return <PageLoader />;
   }

   if (medicalServiceError || taxRatesError || practitionersError || fiscalPrintEnabledError) {
      return (
         <Message
            testId={'medicalServiceUpdateLoaderError'}
            type={MessageType.NEGATIVE}
            label={medicalServiceTranslationKeys.update.form.errors.loadData}
            displayIndicator
         />
      );
   }

   return props.children({
      medicalService: medicalService,
      practitioners: practitioners,
      taxRates: taxRates,
      fiscalPrintEnabled: Boolean(fiscalPrintEnabled),
   });
};

const StyledForColumnsSpan = styled.div`
   grid-column: 1 / span 4;
`;

StyledForColumnsSpan.displayName = 'StyledTwoColumnsSpan';

const StyledForm = styled.form`
   display: grid;
   grid-template-columns: repeat(4, 1fr);
   column-gap: 36px;
   row-gap: 24px;
`;

StyledForm.displayName = 'StyledForm';

export type MedicalServiceUpdateFormBean = MedicalServiceFormBean & {
   id: number;
};

export type MedicalServiceUpdateFormController = FormContextValues<MedicalServiceUpdateFormBean>;

declare type MedicalServiceUpdateFormRendererProps = Pick<
   MedicalServiceUpdateFormController,
   'handleSubmit' | 'formState'
> & {
   setNotUniqueMedicalServiceNameError: VoidFunction;
};

declare type MedicalServiceUpdateFormRenderer = (renderProps: MedicalServiceUpdateFormRendererProps) => JSX.Element;

type MedicalServiceCreateUpdateProps = {
   readonly children: MedicalServiceUpdateFormRenderer;
   readonly medicalService: MedicalServiceWithPractitioners;
   readonly practitioners: Practitioner[];
   readonly taxRates: TaxRate[];
   readonly fiscalPrintEnabled: boolean;
};

// eslint-disable-next-line max-lines-per-function
export const MedicalServiceUpdateForm = (props: MedicalServiceCreateUpdateProps): JSX.Element => {
   const invoiceCreateAvailable = protectFeature('creatingInvoices');
   const controller = useFormController<MedicalServiceUpdateFormBean>(
      useMemo(() => {
         const settlementTypeFormPart = createMedicalServiceSettlementTypeVisitor(
            props.medicalService.defaultSettlementConfiguration.settlementType
         )<Pick<MedicalServiceUpdateFormBean, 'settlementType' | 'fixedValue' | 'proportionalValue'>>({
            visitNone: () => ({
               settlementType: 'None',
               fixedValue: undefined,
               proportionalValue: undefined,
            }),
            visitFixed: () => ({
               settlementType: 'Fixed',
               fixedValue: props.medicalService.defaultSettlementConfiguration.fixedValue!.asNumber(),
               proportionalValue: undefined,
            }),
            visitProportional: () => ({
               settlementType: 'Proportional',
               fixedValue: undefined,
               proportionalValue: props.medicalService.defaultSettlementConfiguration.proportionalValue!,
            }),
         });

         const formData: MedicalServiceUpdateFormBean = {
            id: props.medicalService.id,
            name: props.medicalService.name,
            defaultPrice: props.medicalService.defaultPrice.asNumber(),
            taxRate: getTaxRate(props.taxRates, props.medicalService.taxRateId),
            usingNameAsNameForInvoicing: props.medicalService.usingNameAsNameForInvoicing,
            nameForReceipt: getNameForReceipt(
               props.medicalService.nameForReceipt,
               props.medicalService.name,
               props.medicalService.nameForInvoicing
            ),
            nameForReceiptType: props.medicalService.nameForReceipt.type,
            ...settlementTypeFormPart,
         };

         formData.practitioners = props.medicalService.practitioners.map(
            mapPractitionerMedicalServiceToMedicalServicePractitionerBean(formData)
         );

         return formData;
      }, [props.medicalService, props.taxRates])
   );

   const { settlementType, defaultPrice } = controller.watch(['settlementType', 'defaultPrice']);

   const medicalServiceSettlementTypeVisitor = useMemo<MedicalServiceSettlementTypeVisitor>(
      () => createMedicalServiceSettlementTypeVisitor(settlementType),
      [settlementType]
   );

   const setNotUniqueMedicalServiceNameError = useNotUniqueMedicalServiceNameErrorHandler(controller.setError);
   usePractitionersSynchronization({
      control: controller.control,
      getValue: controller.getValue,
      setValue: controller.setValue,
      trigger: controller.trigger,
      watch: controller.watch,
   });

   return (
      <>
         <FormController controller={controller.register('id')} />
         <StyledForm noValidate autoComplete={'off'}>
            <StyledForColumnsSpan>
               <MedicalServiceNameField name={'name'} register={controller.register} required />
            </StyledForColumnsSpan>

            {nameForInvoicingPartVisible(controller, invoiceCreateAvailable) && (
               <StyledForColumnsSpan>
                  <MedicalServiceNameForInvoicingTextView watch={controller.watch}>
                     <MedicalServiceUsingNameAsNameForInvoicingField
                        name={'usingNameAsNameForInvoicing'}
                        register={controller.register}
                        trigger={controller.trigger}
                     />
                     <MedicalServiceNameForInvoicingField
                        name={'nameForInvoicing'}
                        defaultValue={props.medicalService.nameForInvoicing ?? props.medicalService.name}
                        register={controller.register}
                        setValue={controller.setValue}
                        trigger={controller.trigger}
                        watch={controller.watch}
                     />
                  </MedicalServiceNameForInvoicingTextView>
               </StyledForColumnsSpan>
            )}

            {nameForReceiptPartVisible(controller, props.fiscalPrintEnabled) && (
               <StyledForColumnsSpan>
                  <MedicalServiceNameForReceiptTextView watch={controller.watch}>
                     <MedicalServiceNameForReceiptTypeField
                        name={'nameForReceiptType'}
                        register={controller.register}
                     />
                     <MedicalServiceNameForReceiptField
                        name={'nameForReceipt'}
                        register={controller.register}
                        setValue={controller.setValue}
                        watch={controller.watch}
                     />
                  </MedicalServiceNameForReceiptTextView>
               </StyledForColumnsSpan>
            )}

            <MedicalServiceDefaultPriceField name={'defaultPrice'} register={controller.register} required />
            <MedicalServiceTaxRateField
               name={'taxRate'}
               register={controller.register}
               taxRates={props.taxRates}
               required
            />

            <MedicalServiceSettlementTypeField name={'settlementType'} register={controller.register} required />
            {medicalServiceSettlementTypeVisitor({
               visitNone: () => null,

               visitFixed: () => (
                  <MedicalServiceFixedValueField
                     name={'fixedValue'}
                     register={controller.register}
                     defaultPrice={defaultPrice}
                     trigger={controller.trigger}
                     required
                  />
               ),

               visitProportional: () => (
                  <MedicalServiceProportionalValueField
                     name={'proportionalValue'}
                     register={controller.register}
                     required
                  />
               ),
            })}

            <StyledForColumnsSpan>
               {props.practitioners.length === 1 ? (
                  <MedicalServicePractitionerField
                     register={controller.register}
                     control={controller.control}
                     practitioners={props.practitioners}
                  />
               ) : (
                  <MedicalServicePractitionerInteractiveTable
                     register={controller.register}
                     control={controller.control}
                     practitioners={props.practitioners}
                     medicalServiceName={props.medicalService.name}
                     removeWithConfirmation
                  />
               )}
            </StyledForColumnsSpan>
         </StyledForm>

         {props.children({
            handleSubmit: controller.handleSubmit,
            formState: controller.formState,
            setNotUniqueMedicalServiceNameError: setNotUniqueMedicalServiceNameError,
         })}
      </>
   );
};
